import { ShareAltOutlined } from '@ant-design/icons';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { ConfirmModal } from 'components/Custom/ConfirmModal';
import { runPwaInstallFromUserGesture } from 'utils/pwa-install-flow';
import { isIosDevice, isSamsungInternetBrowser } from 'utils/pwa';
import { usePwaDeferredInstallPrompt } from 'utils/usePwaDeferredInstallPrompt';

export type PwaInstallModalStep =
  | 'confirm'
  | 'ios-guide'
  | 'android-manual'
  | 'use-chrome';

export type PwaInstallModalProps = {
  open: boolean;
  /** Start on a fallback step (settings menu after platform handoff). */
  initialStep?: 'ios-guide' | 'android-manual' | 'use-chrome';
  onClose: () => void;
  /** When set, "Not now" on the confirm step calls this (e.g. persist dismiss). */
  onDismiss?: () => void;
};

export const PwaInstallModal: React.FC<PwaInstallModalProps> = ({
  open,
  initialStep,
  onClose,
  onDismiss,
}) => {
  const { t } = useTranslation();
  const isIos = React.useMemo(() => isIosDevice(), []);
  const useChrome = React.useMemo(() => isSamsungInternetBrowser(), []);
  const deferredInstallPrompt = usePwaDeferredInstallPrompt();
  const [step, setStep] = React.useState<PwaInstallModalStep>(
    initialStep ?? 'confirm'
  );
  const canNativeAndroidInstall =
    !isIos && !useChrome && Boolean(deferredInstallPrompt);
  const siteUrl =
    typeof window !== 'undefined' ? window.location.origin : 'https://fe.generate.tax';

  React.useEffect(() => {
    if (!open) {
      setStep('confirm');
      return;
    }
    if (initialStep) {
      setStep(initialStep);
    }
  }, [open, initialStep]);

  const handleClose = React.useCallback(() => {
    onClose();
    setStep('confirm');
  }, [onClose]);

  const handleDismiss = React.useCallback(() => {
    onDismiss?.();
    handleClose();
  }, [onDismiss, handleClose]);

  const handleInstall = React.useCallback(() => {
    void runPwaInstallFromUserGesture().then((result) => {
      if (result.type === 'android-started') {
        onDismiss?.();
        handleClose();
        return;
      }
      if (result.type === 'ios-guide') {
        setStep('ios-guide');
        return;
      }
      if (result.type === 'use-chrome') {
        setStep('use-chrome');
        return;
      }
      setStep('android-manual');
    });
  }, [onDismiss, handleClose]);

  if (!open) {
    return null;
  }

  if (step === 'ios-guide') {
    return (
      <ConfirmModal
        open
        className="scope-pwa-install-modal"
        title={t('common.pwa-install-title')}
        onCancel={handleClose}
        primaryAction={{
          label: t('common.pwa-install-got-it'),
          onClick: handleClose,
        }}
        showCancel={false}
        closable={false}
        maskClosable={false}
        keyboard={false}
      >
        <div className="scope-pwa-install-modal__guide">
          <p className="scope-pwa-install-modal__intro">
            {t('common.pwa-ios-guide-intro')}
          </p>
          <ol className="scope-pwa-install-modal__steps">
            <li>
              <ShareAltOutlined
                className="scope-pwa-install-modal__share-icon"
                aria-hidden
              />
              {t('common.pwa-ios-step-share')}
            </li>
            <li>{t('common.pwa-ios-step-add')}</li>
            <li>{t('common.pwa-ios-step-confirm')}</li>
          </ol>
        </div>
      </ConfirmModal>
    );
  }

  if (step === 'use-chrome') {
    return (
      <ConfirmModal
        open
        className="scope-pwa-install-modal"
        title={t('common.pwa-install-title')}
        onCancel={handleClose}
        primaryAction={{
          label: t('common.pwa-install-got-it'),
          onClick: handleClose,
        }}
        showCancel={false}
        closable={false}
        maskClosable={false}
        keyboard={false}
      >
        <div className="scope-pwa-install-modal__guide">
          <p className="scope-pwa-install-modal__intro">
            {t('common.pwa-use-chrome-intro')}
          </p>
          <ol className="scope-pwa-install-modal__steps">
            <li>{t('common.pwa-use-chrome-step-open')}</li>
            <li>{t('common.pwa-use-chrome-step-url', { url: siteUrl })}</li>
            <li>{t('common.pwa-use-chrome-step-install')}</li>
          </ol>
        </div>
      </ConfirmModal>
    );
  }

  if (step === 'android-manual') {
    return (
      <ConfirmModal
        open
        className="scope-pwa-install-modal"
        title={t('common.pwa-install-title')}
        onCancel={handleClose}
        primaryAction={{
          label: t('common.pwa-install-got-it'),
          onClick: handleClose,
        }}
        showCancel={false}
        closable={false}
        maskClosable={false}
        keyboard={false}
      >
        <p className="scope-pwa-install-modal__intro">
          {t('common.pwa-android-manual')}
        </p>
      </ConfirmModal>
    );
  }

  return (
    <ConfirmModal
      open
      className="scope-pwa-install-modal"
      title={t('common.pwa-install-title')}
      onCancel={handleDismiss}
      primaryAction={{
        label: isIos || useChrome
          ? t('common.pwa-install-show-steps')
          : t('common.install'),
        onClick: handleInstall,
      }}
      cancelAction={{
        label: t('common.pwa-install-dismiss'),
        onClick: handleDismiss,
      }}
      closable={false}
      maskClosable={false}
      keyboard={false}
    >
      <p className="scope-pwa-install-modal__intro">
        {isIos
          ? t('common.pwa-install-body-ios')
          : useChrome
            ? t('common.pwa-install-body-use-chrome')
            : canNativeAndroidInstall
              ? t('common.pwa-install-body-android-native')
              : t('common.pwa-install-body')}
      </p>
    </ConfirmModal>
  );
};

export default PwaInstallModal;
